import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useQueryAppointableAsPublic,
  useQueryMemberGetCurrentMembershipSubscriptionAsMember,
} from "@gymflow/api";
import { appointableHelper, cn } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import { RouteFeature } from "apps/portal/src/routes";
import { Duration } from "luxon";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { OnboardingContext } from "../../../providers/OnboardingProvider/OnboardingContext";
import useGymflowModels from "../../../store";
import { Button, ClockIcon, LayeredCircles, Spinner } from "../../atoms";
import { ExclamationCircleIcon } from "../../atoms/icons/ExclamationCircleIcon";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import { HostedPagesCard } from "../components/HostedPagesCard";
import HostedPagesOverviewCard from "../components/HostedPagesOverviewCard";

export type HostedPagesAppointmentOverviewProps = {};

export const HostedPagesAppointmentOverview: React.FC<
  HostedPagesAppointmentOverviewProps
> = ({}) => {
  const { currentScreen, setCurrentScreen } = useContext(OnboardingContext);
  const { appointableId } = useParams<{ appointableId: string }>();
  const { createSiteOrEmbedLink, isEmbed } = usePortalRoutes();

  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { userId: id } = useContext(AuthenticationContext);

  const { data: appointable, isLoading } = useQueryAppointableAsPublic({
    api,
    id: parseInt(appointableId),
  });
  const { data: activeSubscription } =
    useQueryMemberGetCurrentMembershipSubscriptionAsMember(
      {
        api,
      },
      { refetchOnWindowFocus: true, enabled: !!id },
    );
  const [parent] = useAutoAnimate();
  const singleHost = appointableHelper.getSingleHost(appointable);

  const nextStepLink = createSiteOrEmbedLink(
    (!singleHost
      ? RouteFeature.AppointableIdSelect
      : RouteFeature.AppointableIdSelectTime
    )
      .replace(":appointableId", appointable?.id?.toString() ?? "")
      .replace(":staffOrFacilityId", singleHost?.toString() ?? ""),
  );
  return (
    <div
      className={cn("track-height flex w-full flex-col overflow-y-auto", {
        "h-fit": isEmbed,
        "h-full": !isEmbed,
      })}
    >
      <HostedPagesBackHeader
        label={t("pages.hostedPagesAppointableOverView.header")}
      />
      <div
        ref={parent}
        className={cn(
          "flex flex-col items-center bg-gray-50 dark:bg-gray-800",
          {
            "h-fit": isEmbed,
            "h-full": !isEmbed,
          },
        )}
      >
        {isLoading && <Spinner />}
        {!isLoading && appointable && (
          <HostedPagesOverviewCard.Container>
            <div className="flex w-full flex-col items-center gap-6">
              <HostedPagesOverviewCard.Header>
                <HostedPagesOverviewCard.HeaderTitle>
                  <div className="flex w-full ">{appointable.name}</div>
                  <div className="flex flex-row items-center gap-1">
                    <ClockIcon
                      pathClassName="stroke-gray-500 stroke-1"
                      className="size-5"
                    />
                    <div className="dark:text-gray-0 text-base font-semibold text-gray-950">
                      {Duration.fromObject({
                        minutes: appointable.duration,
                      })
                        .rescale()
                        .toHuman({
                          unitDisplay: "narrow",
                          listStyle: "short",
                        })}
                    </div>
                  </div>
                </HostedPagesOverviewCard.HeaderTitle>
                {appointable.description && (
                  <HostedPagesOverviewCard.HeaderDescription>
                    {appointable.description}
                  </HostedPagesOverviewCard.HeaderDescription>
                )}
              </HostedPagesOverviewCard.Header>
              <div className="flex w-full flex-row items-center justify-start gap-2">
                <HostedPagesCard.AppointablePrice
                  appointable={appointable}
                  activeSubscription={!!activeSubscription && !!id}
                />
              </div>
              {!!appointable.termsAndConditions && (
                <>
                  <div className="flex w-full flex-row items-center justify-between border-t border-gray-200 dark:border-gray-800" />
                  <div className="flex w-full flex-col gap-2">
                    <div className="flex flex-row items-center gap-2">
                      <LayeredCircles className="size-8" intent="default">
                        <ExclamationCircleIcon pathClassName="stroke-gray-500" />
                      </LayeredCircles>
                      <div className="dark:text-gray-0 font-semibold text-gray-950">
                        {t("page.hostedPagesAppointableOverView.terms")}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {appointable.termsAndConditions}
                    </div>
                  </div>
                </>
              )}
            </div>
            <Button
              className="w-fit px-24"
              {...(currentScreen === "banner"
                ? {
                    onClick: () => {
                      setCurrentScreen("form");
                    },
                  }
                : { link: nextStepLink })}
              intent="secondary"
            >
              {t("page.hostedPagesAppointableOverView.book")}
            </Button>
          </HostedPagesOverviewCard.Container>
        )}
      </div>
    </div>
  );
};
